// client/src/pages/seo/DigitalMenuForRestaurantsNo.jsx
// Route: /no/digital-meny-for-restauranter
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

const sections = [
  { id: "definition", label: "Hva er en digital meny?" },
  { id: "types", label: "Hvilke typer digitale menyer finnes?" },
  { id: "criteria", label: "Hvordan kjenner du igjen en god løsning?" },
  { id: "workflow", label: "Hva endres i den daglige restaurantdriften?" },
  { id: "bestplace", label: "Digital meny med Best Place" },
  { id: "qr", label: "Når trenger en digital meny en QR-kode?" },
  { id: "faq", label: "Vanlige spørsmål om digital meny" },
];

function setMeta(name, content) {
  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function DigitalMenuForRestaurantsNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title = "Digital meny for restauranter | Best Place";
    setMeta(
      "description",
      "Digital meny for restauranter: sammenlign løsninger, administrer priser og retter på nett, vis menyen på mobil og velg riktig leverandør."
    );
    setCanonical("https://best-place.club/no/digital-meny-for-restauranter");
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          en: "/en/digital-menu-for-restaurants",
          de: "/de/digitale-speisekarte",
          no: "/no/digital-meny-for-restauranter",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>Digital meny for restauranter</span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                En digital meny er en restaurantmeny som administreres på nett og som gjestene kan åpne på mobil, nettbrett eller datamaskin. For restaurantdriften er det viktigste hvor raskt innholdet kan oppdateres og hvor godt menyen fungerer for gjestene.
              </p>
              <p className="seoHero__text">
                Når du leter etter en digital meny, handler valget derfor ikke bare om papir eller mobil. Det egentlige spørsmålet er: hvilken løsning kan vedlikeholdes pålitelig i hverdagen, passer restauranten din og samtidig er enkel for gjestene?
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold</h2>
        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">{String(index + 1).padStart(2, "0")}</span>
                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="definition" className="acquaintance">
          <h2>Hva er en digital meny?</h2>
          <p>
            En digital meny er ikke én bestemt teknologi. Begrepet beskriver først og fremst resultatet: en restaurantmeny som er tilgjengelig på nett og kan vedlikeholdes digitalt.
          </p>
          <p>
            Gjestene kan åpne den via en vanlig lenke, restaurantens nettside eller en QR-kode. For restauranten er hovedforskjellen fra en trykt meny at priser, produkter, beskrivelser, bilder og tilgjengelighet kan endres uten nytt trykk.
          </p>
          <p>
            Derfor bør valg av løsning starte med selve menyadministrasjonen, ikke med QR-koden. En QR-kode er bare én av flere måter å åpne den digitale menyen på.
          </p>
        </section>

        <section id="types" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvilke typer digitale menyer finnes?</h2>
            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>1. PDF på nett</h3>
                <p>
                  Den eksisterende trykkfilen publiseres på nett. Det er enkelt, men oppdateringer og lesbarhet på mobil er ofte begrenset.
                </p>
              </div>
              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>2. Mobil nettmeny</h3>
                <p>
                  Kategorier, retter og priser administreres som en ekte mobilside. Enkeltprodukter kan oppdateres uavhengig av resten av menyen.
                </p>
              </div>
              <div className="seoComparison__col">
                <h3>3. Meny med bestillingsfunksjoner</h3>
                <p>
                  Avhengig av systemet kan reservasjon, bestilling, betaling eller andre restaurantfunksjoner kobles til i tillegg til selve menyvisningen.
                </p>
              </div>
            </div>
            <p>
              For mange restauranter er en mobil nettmeny en fleksibel mellomløsning: enklere å vedlikeholde enn en PDF og mindre kompleks enn et komplett bestillings- eller kassasystem.
            </p>
          </div>
        </section>

        <section id="criteria" className="acquaintance">
          <h2>Hvordan kjenner du igjen en god digital meny?</h2>
          <p>
            Gode leverandører skiller seg mindre ut gjennom løftet om å være «digitale» og mer gjennom hvor godt løsningen fungerer i den daglige restaurantdriften.
          </p>
          <ul className="qrArticle__block-li">
            <li className="text__seo-li"><strong>Mobilvisning:</strong> tydelige kategorier, priser og bilder på små skjermer.</li>
            <li className="text__seo-li"><strong>Enkel administrasjon:</strong> personalet kan endre retter og priser selv.</li>
            <li className="text__seo-li"><strong>Tilgjengelighet:</strong> juster utsolgte eller sesongbaserte produkter raskt.</li>
            <li className="text__seo-li"><strong>Flerspråklig støtte:</strong> viktig for internasjonale gjester.</li>
            <li className="text__seo-li"><strong>Egen offentlig URL:</strong> menyen er tilgjengelig via lenke, nettside eller QR-kode.</li>
            <li className="text__seo-li"><strong>Bilder og beskrivelser:</strong> presenter produktene tydelig.</li>
            <li className="text__seo-li"><strong>Pris og begrensninger:</strong> forstå tydelig hvilke funksjoner som er inkludert i abonnementet.</li>
          </ul>
          <p>
            Hvis du vil sammenligne leverandører direkte, kan du bruke siden vår{" "}
            <Link to="/no/digital-meny-sammenligning">Sammenligning av digitale menyer</Link>.
          </p>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva endres i den daglige restaurantdriften med en digital meny?</h2>
            <div className="qrArticle__cardGrid">
              <div>
                <strong>Prisendringer</strong>
                <p>En pris endres én gang i systemet og er deretter oppdatert på nett.</p>
              </div>
              <div>
                <strong>Utsolgte produkter</strong>
                <p>Produkter kan justeres eller skjules før gjestene bestiller.</p>
              </div>
              <div>
                <strong>Sesongtilbud</strong>
                <p>Dagens retter og sesongvarer trenger ikke vente på et nytt opplag.</p>
              </div>
              <div>
                <strong>Internasjonale gjester</strong>
                <p>Avhengig av systemet kan den samme menyen vedlikeholdes på flere språk.</p>
              </div>
            </div>
            <p>
              For konkrete arbeidsflyter for løpende vedlikehold har vi en egen side:{" "}
              <Link to="/no/administrere-meny-online-for-restauranter">administrere en meny på nett</Link>.
            </p>
          </div>
        </section>

        <section id="bestplace" className="acquaintance">
          <h2>Digital meny med Best Place</h2>
          <p>
            Best Place er laget for restauranter som vil administrere menyen på nett og gjøre den tilgjengelig for gjester på mobil. Kategorier, produkter, priser, beskrivelser og bilder redigeres i administrasjonspanelet og vises deretter på den offentlige menysiden.
          </p>
          <div className="qrArticle__cardGrid">
            <div>
              <strong>Menyadministrasjon</strong>
              <p>Administrer kategorier, retter, priser og beskrivelser sentralt.</p>
            </div>
            <div>
              <strong>Flere språk</strong>
              <p>Administrer språkversjoner for internasjonale gjester.</p>
            </div>
            <div>
              <strong>Mobilvisning for gjester</strong>
              <p>Åpne menyen i nettleseren på mobilen.</p>
            </div>
            <div>
              <strong>Tilleggsmoduler</strong>
              <p>Reservasjon og andre funksjoner kan legges til etter behov.</p>
            </div>
          </div>
        </section>

        <section id="qr" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Når trenger en digital meny en QR-kode?</h2>
            <p>
              En QR-kode er spesielt praktisk når gjestene skal åpne menyen direkte ved bordet, ved disken eller fra takeaway-materiale. Men QR-koden er ikke selve den digitale menyen; den er bare én måte å få tilgang til den på.
            </p>
            <p>
              Hvis fokuset ditt er QR-tilgang – opprettelse, skanning, stabil URL og bruk ved bordet – gå til siden vår{" "}
              <Link to="/no/qr-kode-meny-for-restauranter">QR-kode-meny for restauranter</Link> page.
            </p>
          </div>
        </section>

        <section id="faq" className="acquaintance">
          <h2>Vanlige spørsmål om digital meny</h2>
          <div className="seoFaq">
            <details>
              <summary>Er en digital meny det samme som en QR-meny?</summary>
              <p>
                Ikke helt. Den digitale menyen er selve menyen som administreres på nett. En QR-kode er én måte å åpne denne menyen på.
              </p>
            </details>
            <details>
              <summary>Trenger en digital meny en app?</summary>
              <p>
                Nei. En god mobil nettmeny kan åpnes direkte i nettleseren.
              </p>
            </details>
            <details>
              <summary>Kan jeg endre priser når som helst?</summary>
              <p>
                Med en digital meny som kan administreres på nett, bør priser og produkter kunne oppdateres uten å lage en ny PDF eller trykke menyen på nytt.
              </p>
            </details>
            <details>
              <summary>Kan jeg tilby flere språk?</summary>
              <p>
                Ja, hvis systemet du bruker støtter flere språkversjoner.
              </p>
            </details>
            <details>
              <summary>Kan jeg teste en digital meny gratis?</summary>
              <p>
                En test er nyttig for å kontrollere menyadministrasjonen og gjestevisningen før du tar en langsiktig beslutning. Les mer på siden vår{" "}
                <Link to="/no/test-gratis-digital-meny">gratis test av digital meny</Link>.
              </p>
            </details>
          </div>
        </section>

        <section className="articls qrArticle__related">
          <h2>Relaterte temaer</h2>
          <div className="seoCompareSmall">
            <p><Link to="/no/qr-kode-meny-for-restauranter">QR-kode-meny for restauranter</Link></p>
            <p><Link to="/no/digital-meny-sammenligning">Sammenlign digitale menyer</Link></p>
            <p><Link to="/no/flerspraklig-meny-for-restauranter">Multilingual menu</Link></p>
            <p><Link to="/no/administrere-meny-online-for-restauranter">Administrer en meny på nett</Link></p>
          </div>
        </section>
      </article>
    </main>
  );
}